import { useState } from 'react';
import { Button } from '../components/Button/Button';
import { useAppeals, useResolveAppeal, useSuspendedUsers, useUnsuspendUser } from './useModerator';
import type { AppealRow } from './moderatorApi';

/**
 * Admin-only view of the appeals queue + suspended users list.
 *
 * The parent ModeratorDashboard gates by role — moderators see
 * a forbidden state on this tab, admins see the queue.
 */
export function AppealsView({ role }: { role: 'admin' | 'moderator' }): JSX.Element {
  if (role !== 'admin') {
    return (
      <p className="bl-mod__empty">
        Appeals review is admin-only. Ask an admin to look at this queue.
      </p>
    );
  }
  return (
    <div className="bl-mod-appeals">
      <PendingAppeals />
      <SuspendedList />
    </div>
  );
}

function PendingAppeals(): JSX.Element {
  const { data, isPending, error } = useAppeals(true);
  return (
    <section aria-label="Pending appeals" className="bl-mod-appeals__section">
      <h3>Pending appeals</h3>
      {isPending && <p className="bl-mod__status">Loading…</p>}
      {error && (
        <p className="bl-mod__error" role="alert">
          {error.message}
        </p>
      )}
      {!isPending && !error && data && data.length === 0 && (
        <p className="bl-mod__empty">No appeals waiting.</p>
      )}
      {data && data.length > 0 && (
        <ul className="bl-mod-appeals__list">
          {data.map((row) => (
            <AppealCard key={row.id} appeal={row} />
          ))}
        </ul>
      )}
    </section>
  );
}

function AppealCard({ appeal }: { appeal: AppealRow }): JSX.Element {
  const resolve = useResolveAppeal();
  const [note, setNote] = useState('');

  const onClick = (decision: 'overturned' | 'upheld') => () => {
    resolve.mutate({
      appealId: appeal.id,
      payload: {
        decision,
        ...(note.trim() ? { note: note.trim() } : {}),
      },
    });
  };

  return (
    <li className="bl-mod-appeals__row">
      <div className="bl-mod-appeals__head">
        <strong>{appeal.user_email}</strong>
        <time dateTime={appeal.created_at}>{new Date(appeal.created_at).toLocaleString()}</time>
      </div>
      <blockquote className="bl-mod-appeals__quote">{appeal.message}</blockquote>
      <label className="bl-mod-appeals__note">
        <span>Note (optional — visible to the kid on resolve)</span>
        <textarea
          rows={2}
          maxLength={1000}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </label>
      <div className="bl-mod-appeals__actions">
        <Button onClick={onClick('overturned')} disabled={resolve.isPending}>
          {resolve.isPending ? 'Working…' : 'Overturn (lift suspension)'}
        </Button>
        <Button variant="secondary" onClick={onClick('upheld')} disabled={resolve.isPending}>
          Uphold (keep suspended)
        </Button>
      </div>
      {resolve.error && (
        <p className="bl-mod-appeals__error" role="alert">
          {resolve.error.message}
        </p>
      )}
    </li>
  );
}

function SuspendedList(): JSX.Element {
  const { data, isPending, error } = useSuspendedUsers();
  const unsuspend = useUnsuspendUser();
  return (
    <section aria-label="Suspended users" className="bl-mod-appeals__section">
      <h3>Suspended accounts</h3>
      {isPending && <p className="bl-mod__status">Loading…</p>}
      {error && (
        <p className="bl-mod__error" role="alert">
          {error.message}
        </p>
      )}
      {!isPending && !error && data && data.length === 0 && (
        <p className="bl-mod__empty">No accounts on the bench.</p>
      )}
      {data && data.length > 0 && (
        <ul className="bl-mod-appeals__list">
          {data.map((u) => (
            <li key={u.id} className="bl-mod-appeals__row">
              <div className="bl-mod-appeals__head">
                <strong>{u.email}</strong>
                <span>{u.role}</span>
                {u.suspended_at && (
                  <time dateTime={u.suspended_at}>{new Date(u.suspended_at).toLocaleString()}</time>
                )}
              </div>
              <div className="bl-mod-appeals__actions">
                <Button
                  variant="secondary"
                  onClick={() => unsuspend.mutate({ userId: u.id })}
                  disabled={unsuspend.isPending}
                >
                  {unsuspend.isPending ? 'Working…' : 'Unsuspend'}
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
